import { ChevronLeft, ChevronRight, CloudUpload, Info, Plus, Trash2 } from "lucide-react";
import React, { useState, useRef, Activity } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

function excelDateToJSDate(serial) {
  if (!serial || isNaN(serial)) return serial;
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date_info = new Date(utc_value * 1000);
  if (isNaN(date_info)) return serial;
  const day = String(date_info.getUTCDate()).padStart(2, "0");
  const month = String(date_info.getUTCMonth() + 1).padStart(2, "0");
  const year = date_info.getUTCFullYear();
  return `${day}-${month}-${year}`;
}

export default function CsvXlsxImporter({
  title = "",
  subDescription = "Batas ukuran file 1mb",
  columns = [],
  rules = {},
  onUpload = () => {},
  navigateTo = "/",
  maxLimit = null,
}) {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const firstErrorRef = useRef(null);

  const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

  /** 🔧 Cek batas file */
  const checkFileLimit = (file) => {
    if (!maxLimit) return true; // jika tidak ada limit, skip
    const fileSizeMB = bytesToMB(file.size);
    const limitMB = parseFloat(maxLimit);
    if (fileSizeMB > limitMB) {
      toast.error(`Ukuran file terlalu besar (${fileSizeMB} MB). Maksimal ${limitMB} MB.`);
      return false;
    }
    return true;
  };

  const handleFile = (file) => {
    if (!checkFileLimit(file)) return;
    const reader = new FileReader();
    const isCsv = file.name.endsWith(".csv");
    reader.onload = (e) => {
      const result = e.target.result;
      let parsed = [];

      if (isCsv) {
        const workbook = XLSX.read(result, { type: "string" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const fileHeaders = Object.keys(rows[0] || {});

        parsed = rows.map((row) => {
          const newRow = {};
          for (let key of fileHeaders) {
            let val = row[key];

            // handle excel date
            if (!isNaN(val) && val > 40000 && val < 60000) {
              val = excelDateToJSDate(Number(val));
            }

            newRow[key?.trim()] = val;
          }
          return newRow;
        });

        setHeaders(columns.length ? columns : fileHeaders);
      } else {
        const workbook = XLSX.read(result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const fileHeaders = Object.keys(rows[0] || {});
        parsed = rows.map((row) => {
          const newRow = {};
          for (let key of fileHeaders) {
            let val = row[key];
            if (!isNaN(val) && val > 40000 && val < 60000) {
              val = excelDateToJSDate(Number(val));
            }
            newRow[key?.trim()] = val;
          }
          return newRow;
        });
        setHeaders(columns.length ? columns : fileHeaders);
      }
      setData(parsed);
      setErrors({});
      setCurrentPage(1);
    };

    document.getElementById("fileInput").value = "";

    if (isCsv) reader.readAsText(file);
    else reader.readAsArrayBuffer(file);
  };

  /** 🔍 Validasi rules per kolom */
  const validateField = (key, value) => {
    const rule = rules[key];
    if (!rule) return null;

    if (rule.required && (!value || value.trim() === "")) {
      return "Wajib diisi";
    }

    if (rule.type) {
      switch (rule.type) {
        case "number":
          if (isNaN(value)) return "Harus berupa angka";
          break;
        case "email":
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Format email tidak valid";
          break;
        case "date":
          if (isNaN(Date.parse(value))) return "Format tanggal tidak valid";
          break;
        default:
          break;
      }
    }

    if (rule.pattern && !rule.pattern.test(value)) {
      return rule.message || "Format tidak valid";
    }

    return null;
  };

  const validateData = () => {
    const newErrors = {};
    data.forEach((row, rowIndex) => {
      for (const key of headers) {
        const errorMsg = validateField(key, row[key]);
        if (errorMsg) newErrors[`${rowIndex}-${key}`] = errorMsg;
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      const [rowIndex] = firstKey.split("-");
      const targetPage = Math.floor(rowIndex / rowsPerPage) + 1;
      setCurrentPage(targetPage);
      setTimeout(() => {
        firstErrorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200);
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!validateData()) return;

    try {
      setLoading(true);

      if (typeof onUpload === "function") {
        // ⏳ Tunggu fungsi eksternal (misalnya API POST)
        await Promise.resolve(onUpload(data));
        toast.success("Data berhasil diupload!");
        navigate(navigateTo);
      } else {
        // fallback simulasi jika tidak ada prop
        console.log("Uploading data:", data);
        toast.success("Data berhasil diupload!");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Gagal upload data!");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, key, value) => {
    setData((prev) => prev.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  };

  const handleDelete = (index) => {
    setData((prev) => prev.filter((_, i) => i !== index));
  };
  const handleAppend = () => {
    if (!headers?.length) {
      setHeaders(columns);
      const headerFromCol = Object.keys(columns || {});
      const emptyRow = Object.fromEntries(headerFromCol.map((c) => [c, ""]));
      setData((prev) => [...prev, emptyRow]);
      const lastPage = Math.ceil((data.length + 1) / rowsPerPage);
      setCurrentPage(lastPage);
      return;
    }
    const emptyRow = Object.fromEntries(headers.map((h) => [h, ""]));
    setData((prev) => [...prev, emptyRow]);
    const lastPage = Math.ceil((data.length + 1) / rowsPerPage);
    setCurrentPage(lastPage);
  };

  const paginated = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const totalPages = Math.ceil(data.length / rowsPerPage);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  return (
    <>
      <Activity mode={data.length > 0 ? "visible" : "hidden"}>
        <>
          <div className="flex items-center justify-end gap-4 my-2">
            <button
              onClick={handleAppend}
              className="bg-white text-green-600 border border-green-600 cursor-pointer px-4 py-2 hover:bg-green-600 hover:text-white rounded-lg flex items-center gap-2"
            >
              <Plus size={18} /> Tambah Baris
            </button>
            {data.length > 0 && (
              <button
                onClick={() => {
                  if (confirm("Hapus semua data import?")) {
                    setData([]);
                    setErrors({});
                    setCurrentPage(1);
                  }
                }}
                className="bg-white text-red-600 border border-red-600 cursor-pointer px-4 py-2 hover:bg-red-600 hover:text-white rounded-lg flex items-center gap-2"
              >
                <Trash2 size={18} /> Hapus Semua
              </button>
            )}
            <button
              onClick={handleUpload}
              disabled={loading}
              className="bg-blue-600 text-white border border-blue-600 cursor-pointer px-4 py-2 hover:bg-blue-700 rounded-lg flex items-center gap-2"
            >
              <Activity mode={loading ? "visible" : "hidden"}>
                <>
                  <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                  <span>Mengunggah...</span>
                </>
              </Activity>
              <Activity mode={!loading ? "visible" : "hidden"}>
                <CloudUpload size={18} /> Upload
              </Activity>
            </button>
          </div>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-100 text-left">
                <tr>
                  {headers.map((h) => (
                    <th key={h} className="p-2 font-medium">
                      {h}
                      {rules[h]?.required && <span className="text-red-500">*</span>}
                    </th>
                  ))}
                  <th className="p-2 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row, i) => {
                  const actualIndex = (currentPage - 1) * rowsPerPage + i;
                  return (
                    <tr key={actualIndex} className="odd:bg-white even:bg-gray-100">
                      {headers.map((h) => {
                        const cellKey = `${actualIndex}-${h}`;
                        const errorMsg = errors[cellKey];
                        const isFirstError = errorMsg && cellKey === Object.keys(errors)[0];
                        return (
                          <td
                            key={h}
                            className={`p-2 align-top ${errorMsg ? "bg-red-100" : ""}`}
                            ref={isFirstError ? firstErrorRef : null}
                          >
                            <input
                              id={cellKey}
                              name={cellKey}
                              type="text"
                              value={row[h] || ""}
                              onChange={(e) => handleChange(actualIndex, h, e.target.value)}
                              className={`border rounded p-1 w-full ${
                                errorMsg ? "border-red-500" : "border-gray-300"
                              }`}
                            />
                            {errorMsg && (
                              <div className="text-red-500 text-xs mt-1">{errorMsg}</div>
                            )}
                          </td>
                        );
                      })}
                      <td className="p-2 text-center">
                        <button
                          onClick={() => handleDelete(actualIndex)}
                          className="text-red-600 cursor-pointer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex justify-end items-center p-3 text-sm text-gray-700 gap-2">
              <span>
                Menampilkan{" "}
                <strong>
                  {(currentPage - 1) * rowsPerPage + 1}–
                  {Math.min(currentPage * rowsPerPage, data.length)}
                </strong>{" "}
                dari <strong>{data.length}</strong>
              </span>

              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <ChevronLeft />
              </button>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        </>
      </Activity>

      {/* Drop zone */}
      <Activity mode={data.length > 0 ? "hidden" : "visible"}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 h-[80vh]">
          <div className="flex flex-col items-start">
            {title && <h2 className="text-2xl font-semibold text-gray-800">{title}</h2>}
            <p className="text-gray-600">Berkas yang diterima harus berformat CSV/XLSX</p>
            <p className="text-blue-700 w-full bg-blue-50 p-2 rounded-lg mt-5 flex items-center gap-2">
              <Info size={18} />
              <span>Pastikan berkas anda mencakup semua kolom yang diperlukan.</span>
            </p>

            {columns.length > 0 && (
              <div className="mt-5 w-full">
                <h3 className="text-gray-600 border-b border-gray-400 pb-2 mb-2 text-base">
                  Kolom yang diperlukan:
                </h3>
                <ul className="list-none">
                  {columns.map((col) => (
                    <li
                      key={col}
                      className="text-gray-600 py-1.5 flex justify-between items-center gap-2"
                    >
                      <span className="font-normal text-base">{col}</span>
                      {rules[col] && rules[col].required && <Info size={18} />}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <label
            htmlFor="fileInput"
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className="border-2 border-dashed border-gray-400 p-6 rounded-lg text-center text-gray-600 hover:bg-gray-50 flex flex-col gap-3 items-center justify-center"
          >
            <input
              type="file"
              accept=".csv, .xlsx"
              onChange={(e) => handleFile(e.target.files[0])}
              className="hidden"
              id="fileInput"
            />
            <CloudUpload className="text-blue-600" size={40} />
            <h3 className="text-base font-semibold">Seret dan lepas berkas</h3>
            <h4 className="text-sm font-normal">{subDescription}</h4>
            <span className="cursor-pointer bg-blue-600 rounded-lg px-4 py-2 text-white">
              Cari Berkas
            </span>
            <button
              type="button"
              className="cursor-pointer text-blue-600 hover:underline"
              onClick={handleAppend}
            >
              atau tambahkan data manual
            </button>
          </label>
        </div>
      </Activity>
    </>
  );
}
